import React, { useEffect, useState } from 'react';
import RolePickMatrix from '../../components/RolePickMatrix';
import {
    ApplicableRole,
    applyAccess,
    fetchApplicableRoles,
} from '../../api/access';
import { ApiError } from '../../api/request';
import { useAuthz, useMe } from '../../store';
import { toast } from '../../utils/toast';

/**
 * 我的权限 (2026-09-13, 左侧菜单, 非系统管理员可见):
 * 上半部分是自己持有的角色与说明 (随 my-roles 下发, 不调管理接口);
 * 下半部分直接申请更多权限 —— 与首次申请页同一套 RolePickMatrix, 提交后进审批中心 (工单动作 apply_roles),
 * 管理员通过后角色追加绑定、权限快照即时刷新.
 */
const MyPermissions = () => {
    const authz = useAuthz();
    const me = useMe();
    const [roles, setRoles] = useState<ApplicableRole[]>([]);
    const [loading, setLoading] = useState(true);
    const [picked, setPicked] = useState<string[]>([]);
    const [reason, setReason] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [submitted, setSubmitted] = useState<string | null>(null);
    // 角色 → 带来该角色的用户组显示名 (2026-09-13): 同一角色既直接绑又经组获得时两处都列.
    const groupsOfRole = (role: string) =>
        (authz.groups || [])
            .filter((g) => g.roles.includes(role))
            .map((g) => g.displayName || g.name);

    useEffect(() => {
        setLoading(true);
        fetchApplicableRoles()
            .then((r) =>
                setRoles((r.list || []).filter((x) => x.name !== 'user')),
            )
            .catch(() => setRoles([]))
            .finally(() => setLoading(false));
    }, []);

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (picked.length === 0) {
            setError('请至少勾选一项权限');
            return;
        }
        setBusy(true);
        setError('');
        try {
            await applyAccess(reason.trim(), picked);
            toast('已提交申请, 管理员在审批中心通过后生效');
            setSubmitted(
                picked
                    .map(
                        (n) =>
                            roles.find((r) => r.name === n)?.displayName || n,
                    )
                    .join('、'),
            );
            setPicked([]);
            setReason('');
        } catch (err) {
            setError(
                err instanceof ApiError
                    ? `${err.code}: ${err.message}`
                    : String(err),
            );
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="space-y-4">
            <div>
                <h1 className="text-lg font-semibold text-slate-800">
                    我的权限
                </h1>
                <div className="text-xs text-slate-500">
                    {me.displayName || me.username} · 当前持有{' '}
                    {authz.roles.length} 个角色;权限按资源域授予, 写操作仍走审批
                </div>
            </div>

            {!authz.rbacEnabled && (
                <div className="rounded border border-sky-200 bg-sky-50 p-3 text-xs text-sky-800">
                    当前平台运行在老权限模型下(资源域级权限未启用):管理员可操作全部功能,
                    其余已准入用户可使用全部业务功能、写操作照旧走审批。下面列出的角色暂不生效。
                </div>
            )}

            <div className="card p-4">
                <div className="text-sm font-semibold mb-3">持有的角色</div>
                <div className="rounded-lg border border-slate-200 divide-y divide-slate-100">
                    {authz.roles.map((r) => (
                        <div key={r} className="px-4 py-3">
                            <div className="text-sm font-medium text-slate-800">
                                {authz.roleLabels[r] || r}
                                <span className="ml-2 font-mono text-xs text-slate-400">
                                    {r}
                                </span>
                                {groupsOfRole(r).map((g) => (
                                    <span
                                        key={g}
                                        className="tag ml-2 bg-violet-50 text-violet-700"
                                        title="该角色来自用户组"
                                    >
                                        来自用户组 · {g}
                                    </span>
                                ))}
                            </div>
                            {authz.roleDescriptions[r] && (
                                <div className="mt-0.5 text-xs text-slate-500">
                                    {authz.roleDescriptions[r]}
                                </div>
                            )}
                        </div>
                    ))}
                    {authz.roles.length === 0 && (
                        <div className="px-4 py-3 text-xs text-slate-400">
                            尚未分配任何角色
                        </div>
                    )}
                </div>
            </div>

            {/* 系统管理员全权, 无需申请; 直接敲 URL 进来只看角色 */}
            {!me.isAdmin && (
                <form onSubmit={submit} className="card p-4 space-y-4">
                    <div>
                        <div className="text-sm font-semibold">
                            申请更多权限
                        </div>
                        <div className="text-xs text-slate-500">
                            勾选需要的模块只读或资源管理员,
                            管理员在审批中心通过后即时生效;已持有的不再列出
                        </div>
                    </div>
                    {submitted && (
                        <div className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded p-2">
                            已提交申请:{submitted}。可在「我的工单」跟踪进度,
                            通过后刷新页面生效。
                        </div>
                    )}
                    <RolePickMatrix
                        roles={roles}
                        picked={picked}
                        onChange={setPicked}
                        held={authz.roles}
                        loading={loading}
                        emptyHint="没有可追加申请的角色: 你已拥有全部资源的管理权限"
                    />
                    <div>
                        <div className="mb-1 text-xs text-slate-500">
                            申请理由(必填,最多 200 字)
                        </div>
                        <textarea
                            className="inp w-full h-20"
                            maxLength={200}
                            placeholder="例如:负责 XX 系列材料的参数录入,需要材料 / 工艺参数管理权限"
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                        />
                    </div>
                    {error && (
                        <div className="text-sm text-rose-600">{error}</div>
                    )}
                    <div className="flex items-center justify-between">
                        <div className="text-xs text-slate-400">
                            已选 {picked.length} 项
                        </div>
                        <button
                            type="submit"
                            className="btn-primary"
                            disabled={
                                busy || picked.length === 0 || !reason.trim()
                            }
                        >
                            {busy ? '提交中…' : '提交申请'}
                        </button>
                    </div>
                </form>
            )}
        </div>
    );
};

export default MyPermissions;
